import type { CSSProperties, ForwardedRef, ReactNode, Ref, RefObject } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { DatePickerProps as AriaDatePickerProps, DateValue } from 'react-aria-components/DatePicker';
import { DatePicker as AriaDatePicker, DatePickerStateContext, Text } from 'react-aria-components/DatePicker';
import type { DateRangePickerProps as AriaDateRangePickerProps } from 'react-aria-components/DateRangePicker';
import {
	DateRangePicker as AriaDateRangePicker,
	DateRangePickerStateContext,
} from 'react-aria-components/DateRangePicker';
import { FormContext } from 'react-aria-components/Form';
import { GroupContext as AriaGroupContext } from 'react-aria-components/Group';
import { PopoverContext } from 'react-aria-components/Popover';
import type { ContextValue } from 'react-aria-components/slots';
import { Provider, useSlottedContext } from 'react-aria-components/slots';
import { useLocale } from 'react-aria/I18nProvider';
import { mergeRefs, useResizeObserver } from '@react-aria/utils';
import { cva, cx } from 'class-variance-authority';

import { Icon } from '@launchpad-ui/icons';

import { ButtonContext } from './Button';
import { useLPContextProps } from './utils';

import baseStyles from './styles/base.module.css';
import styles from './styles/DatePicker.module.css';

const datePickerStyles = cva(styles.picker);

interface DatePickerProps<T extends DateValue> extends AriaDatePickerProps<T> {
	ref?: Ref<HTMLDivElement>;
}

interface DateRangePickerProps<T extends DateValue> extends AriaDateRangePickerProps<T> {
	ref?: Ref<HTMLDivElement>;
}

const DatePickerContext = createContext<ContextValue<DatePickerProps<DateValue>, HTMLDivElement>>(null);
const DateRangePickerContext = createContext<ContextValue<DateRangePickerProps<DateValue>, HTMLDivElement>>(null);

interface PickerTriggerProviderProps {
	children: ReactNode;
	trigger: 'DatePicker' | 'DateRangePicker';
	buttonRef: RefObject<HTMLButtonElement | null>;
	isDisabled: boolean;
	isInvalid: boolean;
	isInForm: boolean;
}

function readPickerTriggerElement(
	buttonRef: RefObject<HTMLButtonElement | null>,
	groupRef: ForwardedRef<HTMLDivElement> | undefined,
): Element | null {
	return buttonRef.current ?? (groupRef && 'current' in groupRef ? groupRef.current : null);
}

class LiveElementRef {
	constructor(private readonly readCurrent: () => Element | null) {}

	get current(): Element | null {
		return this.readCurrent();
	}
}

const PickerTriggerProvider = ({
	children,
	trigger,
	buttonRef,
	isDisabled,
	isInvalid,
	isInForm,
}: PickerTriggerProviderProps) => {
	const groupRef = useSlottedContext(AriaGroupContext)?.ref;
	const outerButton = useSlottedContext(ButtonContext);
	const outerButtonRef = outerButton?.ref;
	const formButtonRef = useMemo(() => mergeRefs(buttonRef, outerButtonRef), [buttonRef, outerButtonRef]);
	const triggerRef = useMemo(
		() => new LiveElementRef(() => readPickerTriggerElement(buttonRef, groupRef)),
		[buttonRef, groupRef],
	);

	const [triggerWidth, setTriggerWidth] = useState<string | null>(null);
	const onResize = useCallback(() => {
		const element = triggerRef.current;
		if (element instanceof HTMLElement) {
			setTriggerWidth(`${element.offsetWidth}px`);
		}
	}, [triggerRef]);

	useResizeObserver({
		ref: triggerRef as RefObject<HTMLElement | null>,
		onResize,
	});

	return (
		<Provider
			values={[
				[
					ButtonContext,
					isInForm
						? { ...outerButton, ref: formButtonRef }
						: {
								ref: buttonRef,
								isDisabled,
								className: cx(isInvalid && baseStyles.invalid),
								variant: 'picker',
								size: null,
							},
				],
				[
					PopoverContext,
					{
						trigger,
						triggerRef,
						placement: 'bottom start',
						style: { '--trigger-width': triggerWidth } as CSSProperties,
					},
				],
			]}
		>
			{children}
		</Provider>
	);
};

/**
 * A date picker combines a DateField and a Calendar popover to allow users to enter or select a date and time value.
 *
 * https://react-spectrum.adobe.com/react-aria/DatePicker.html
 */
const DatePicker = <T extends DateValue>({ ref, ...props }: DatePickerProps<T>) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, DatePickerContext);
	const formContext = useSlottedContext(FormContext);
	const buttonRef = useRef<HTMLButtonElement>(null);

	return (
		<AriaDatePicker
			{...mergedProps}
			ref={mergedRef}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				datePickerStyles({ ...renderProps, className }),
			)}
		>
			{composeRenderProps(mergedProps.children, (children, { isDisabled, isInvalid }) => (
				<PickerTriggerProvider
					trigger="DatePicker"
					buttonRef={buttonRef}
					isDisabled={isDisabled}
					isInvalid={isInvalid}
					isInForm={Boolean(formContext)}
				>
					{children}
				</PickerTriggerProvider>
			))}
		</AriaDatePicker>
	);
};

/**
 * A date range picker combines two DateFields and a RangeCalendar popover to allow users to enter or select a date and time range.
 *
 * https://react-spectrum.adobe.com/react-aria/DateRangePicker.html
 */
const DateRangePicker = <T extends DateValue>({ ref, ...props }: DateRangePickerProps<T>) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, DateRangePickerContext);
	const formContext = useSlottedContext(FormContext);
	const buttonRef = useRef<HTMLButtonElement>(null);

	return (
		<AriaDateRangePicker
			{...mergedProps}
			ref={mergedRef}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				datePickerStyles({ ...renderProps, className }),
			)}
		>
			{composeRenderProps(mergedProps.children, (children, { isDisabled, isInvalid }) => (
				<PickerTriggerProvider
					trigger="DateRangePicker"
					buttonRef={buttonRef}
					isDisabled={isDisabled}
					isInvalid={isInvalid}
					isInForm={Boolean(formContext)}
				>
					{children}
				</PickerTriggerProvider>
			))}
		</AriaDateRangePicker>
	);
};

const DatePickerValue = () => {
	const state = useContext(DatePickerStateContext);
	const { locale } = useLocale();
	const date = state?.formatValue(locale, { month: 'short' });

	return <Text className={styles.value}>{date}</Text>;
};

const DateRangePickerValue = () => {
	const state = useContext(DateRangePickerStateContext);
	const { locale } = useLocale();
	const date = state?.formatValue(locale, { month: 'short' });

	return date?.start === date?.end ? (
		<Text className={styles.value}>{date?.end}</Text>
	) : (
		<>
			<Text>{date?.start}</Text>
			<Icon name="arrow-right-thin" size="small" />
			<Text className={styles.value}>{date?.end}</Text>
		</>
	);
};

export {
	DatePicker,
	DatePickerContext,
	DateRangePicker,
	DatePickerValue,
	DateRangePickerContext,
	DateRangePickerValue,
	datePickerStyles,
};
export type { DatePickerProps, DateRangePickerProps };
